import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  CalendarToday as DateIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { consentService, ConsentType, ConsentResponse } from '../services/consentService';

interface ConsentFormDisplay extends ConsentType {
  version: string;
  effectiveDate: string;
  expiryDate?: string;
  createdBy: string;
  usageCount?: number;
  isActive: boolean;
}

interface ConsentRecordDisplay extends ConsentResponse {
  studentId: number;
  studentName: string;
  formId: number;
  formTitle: string;
  formVersion: string;
  consentedAt: string;
  statusDisplay: 'active' | 'expired' | 'withdrawn';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`consent-tabpanel-${index}`}
      aria-labelledby={`consent-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ConsentManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState<ConsentFormDisplay[]>([]);
  const [records, setRecords] = useState<ConsentRecordDisplay[]>([]);
  const [selectedForm, setSelectedForm] = useState<ConsentFormDisplay | null>(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    version: '',
    content: '',
    isMandatory: false,
    effectiveDate: new Date(),
    expiryDate: null as Date | null
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load consent types from API
      const consentTypes = await consentService.getConsentTypes();
      
      // Transform consent types to display format
      const formsDisplay: ConsentFormDisplay[] = consentTypes.map(type => ({
        ...type,
        title: type.name,
        version: '1.0', // Version not in API, using default
        effectiveDate: type.createdAt,
        createdBy: `User ${type.createdByUserId}`,
        usageCount: 0, // Not available in current API
        isActive: true // Assuming all returned types are active
      }));
      
      setForms(formsDisplay);
      
      // Get expiring consents for admin view
      try {
        const expiringConsents = await consentService.getExpiringConsents();
        const recordsDisplay: ConsentRecordDisplay[] = expiringConsents.map(consent => ({
          ...consent,
          studentId: consent.userId,
          studentName: `User ${consent.userId}`,
          formId: consent.consentTypeId,
          formTitle: consent.consentType?.name || 'Unknown',
          formVersion: '1.0',
          consentedAt: consent.givenAt,
          statusDisplay: consent.status === 'GIVEN' ? 'active' : 
                        consent.status === 'EXPIRED' ? 'expired' : 'withdrawn'
        }));
        setRecords(recordsDisplay);
      } catch (error) {
        // If admin endpoint fails, just show empty records
        setRecords([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load consent data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = () => {
    setFormData({
      title: '',
      version: '1.0',
      content: '',
      isMandatory: false,
      effectiveDate: new Date(),
      expiryDate: null
    });
    setSelectedForm(null);
    setEditDialog(true);
  };

  const handleEditForm = (form: ConsentFormDisplay) => {
    setFormData({
      title: form.name,
      version: form.version,
      content: form.content,
      isMandatory: form.isMandatory,
      effectiveDate: new Date(form.effectiveDate),
      expiryDate: form.expiryDate ? new Date(form.expiryDate) : null
    });
    setSelectedForm(form);
    setEditDialog(true);
  };

  const handleSaveForm = async () => {
    try {
      if (!selectedForm) {
        // Create new consent type
        await consentService.createConsentType({
          name: formData.title,
          code: formData.title.toLowerCase().replace(/\s+/g, '_'),
          description: formData.title,
          content: formData.content,
          isMandatory: formData.isMandatory,
          requiresSignature: false,
          expiryDays: formData.expiryDate ? 
            Math.ceil((formData.expiryDate.getTime() - formData.effectiveDate.getTime()) / (1000 * 60 * 60 * 24)) : 
            undefined
        });
        toast.success('Consent form created');
      } else {
        // Update not supported in current API
        toast.info('Consent form update not available in current API');
      }
      setEditDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save consent form');
    }
  };

  const handleToggleActive = async (form: ConsentFormDisplay) => {
    try {
      // API call would go here
      toast.success(`Consent form ${form.isActive ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update form status');
    }
  };

  const handleDeleteForm = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this consent form?')) {
      try {
        // API call would go here
        toast.success('Consent form deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete consent form');
      }
    }
  };

  const handleExportRecords = () => {
    // Export functionality
    toast.info('Exporting consent records...');
  };

  const renderFormsTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Version</TableCell>
            <TableCell align="center">Type</TableCell>
            <TableCell>Effective Date</TableCell>
            <TableCell>Expiry Date</TableCell>
            <TableCell align="center">Usage</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {forms.map((form) => (
            <TableRow key={form.id}>
              <TableCell>
                <Typography variant="subtitle2">{form.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Created by {form.createdBy}
                </Typography>
              </TableCell>
              <TableCell>{form.version}</TableCell>
              <TableCell align="center">
                <Chip 
                  label={form.isMandatory ? 'Mandatory' : 'Optional'} 
                  color={form.isMandatory ? 'error' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>{format(new Date(form.effectiveDate), 'MMM dd, yyyy')}</TableCell>
              <TableCell>
                {form.expiryDate 
                  ? format(new Date(form.expiryDate), 'MMM dd, yyyy')
                  : '-'
                }
              </TableCell>
              <TableCell align="center">{form.usageCount || 0}</TableCell>
              <TableCell align="center">
                <Chip 
                  icon={form.isActive ? <ActiveIcon /> : <InactiveIcon />}
                  label={form.isActive ? 'Active' : 'Inactive'} 
                  color={form.isActive ? 'success' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  <Tooltip title="View">
                    <IconButton 
                      size="small"
                      onClick={() => {
                        setSelectedForm(form);
                        setViewDialog(true);
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton 
                      size="small"
                      onClick={() => handleEditForm(form)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplicate">
                    <IconButton size="small">
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={form.isActive ? 'Deactivate' : 'Activate'}>
                    <IconButton 
                      size="small"
                      onClick={() => handleToggleActive(form)}
                      color={form.isActive ? 'error' : 'success'}
                    >
                      {form.isActive ? <InactiveIcon /> : <ActiveIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small"
                      color="error"
                      onClick={() => handleDeleteForm(form.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderRecordsTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Student</TableCell>
            <TableCell>Consent Form</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Consented Date</TableCell>
            <TableCell>IP Address</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((record) => (
            <TableRow key={record.id}>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2">{record.studentName}</Typography>
                </Box>
              </TableCell>
              <TableCell>{record.formTitle}</TableCell>
              <TableCell>{record.formVersion}</TableCell>
              <TableCell>
                {format(new Date(record.consentedAt), 'MMM dd, yyyy h:mm a')}
              </TableCell>
              <TableCell>{record.ipAddress || '-'}</TableCell>
              <TableCell align="center">
                <Chip 
                  label={record.statusDisplay} 
                  color={
                    record.statusDisplay === 'active' ? 'success' : 
                    record.statusDisplay === 'expired' ? 'warning' : 'error'
                  }
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  <Tooltip title="View Details">
                    <IconButton size="small">
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download PDF">
                    <IconButton size="small">
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View History">
                    <IconButton size="small">
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Consent Form Management
        </Typography>
        <Box display="flex" gap={2}>
          {tabValue === 1 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportRecords}
            >
              Export Records
            </Button>
          )}
          {tabValue === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateForm}
            >
              Create Form
            </Button>
          )}
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h4">{forms.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Forms
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h4">
                  {forms.filter(f => f.isActive).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Forms
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h4">{records.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Consents
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Consent Forms" />
          <Tab label="Consent Records" />
        </Tabs>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            {forms.length === 0 ? (
              <Alert severity="info">
                No consent forms created yet. Click "Create Form" to get started.
              </Alert>
            ) : (
              renderFormsTable()
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {records.length === 0 ? (
              <Alert severity="info">
                No consent records found.
              </Alert>
            ) : (
              renderRecordsTable()
            )}
          </TabPanel>
        </>
      )}

      {/* View Form Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedForm?.name}</DialogTitle>
        <DialogContent>
          {selectedForm && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Version</Typography>
                  <Typography variant="body1">{selectedForm.version}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Typography variant="body1">
                    {selectedForm.isMandatory ? 'Mandatory' : 'Optional'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Effective Date</Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedForm.effectiveDate), 'MMMM dd, yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Usage Count</Typography>
                  <Typography variant="body1">{selectedForm.usageCount || 0}</Typography>
                </Grid>
              </Grid>
              
              <Typography variant="subtitle2" color="text.secondary">Content</Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedForm.content}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Create Form Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedForm ? 'Edit Consent Form' : 'Create Consent Form'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Version"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Consent Content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Effective Date"
                  value={formData.effectiveDate}
                  onChange={(newValue) => setFormData({ ...formData, effectiveDate: newValue || new Date() })}
                  slotProps={{
                    textField: { fullWidth: true, required: true }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiry Date (Optional)"
                  value={formData.expiryDate}
                  onChange={(newValue) => setFormData({ ...formData, expiryDate: newValue })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isMandatory}
                    onChange={(e) => setFormData({ ...formData, isMandatory: e.target.checked })}
                  />
                }
                label="Mandatory consent (required for all students)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveForm}>
            {selectedForm ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ConsentManagementPage;